import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import "./travel_edit.css";
import { useDispatch, useSelector } from 'react-redux';

const TravelEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
    const isAdmin = useSelector(state => state.permission.isAdmin);
  const url = process.env.REACT_APP_API_URL;


  const modules = {
    toolbar: [
      [{ 'font': [] }],
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ]
  };
  
  const formats = [
    'font',
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet'
  ];
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Utazás adatok szerkesztéséhez (inputok, dátumok stb.)
  const [title, setTitle] = useState('');
  // Leírás ReactQuill segítségével (HTML tartalom)
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [tol, setTol] = useState(new Date());
  const [ig, setIg] = useState(new Date());
  // Ha be van pipálva, az utazás a "Repülős utak" kategóriába kerül
  const [repulos, setRepulos] = useState(false);

  // Háttérkép: lehet fájl vagy URL
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [backgroundLink, setBackgroundLink] = useState('');
  
  // Galéria képek: feltöltött fájlok és URL-ek
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryLinks, setGalleryLinks] = useState(['']);
  
  // Már létező, backendről érkező galéria képek
  const [existingGalleryImages, setExistingGalleryImages] = useState([]);
  
  // Lekérjük a meglévő utazás adatait a backendből
  useEffect(() => {
    async function fetchTravel() {
      try {
        const response = await fetch(url + `/api/utazasok/${id}`);
        if (!response.ok) {
          throw new Error('Nem sikerült lekérni az utazás adatait.');
        }
        const data = await response.json();
        // Állapotok kitöltése a backendről kapott adatokkal
        setTitle(data.title);
        setDescription(data.description);  // HTML tartalom, ReactQuill szerkesztője
        setPrice(data.price);
        setTol(data.tol);
        setIg(data.ig);
        setBackgroundLink(data.background_picture);
        setRepulos(data.repulos === true);
        if (data.images) {
          setExistingGalleryImages(data.images);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchTravel();
  }, [id]);
  
  // Fájlok és URL-ek kezelése
  const handleBackgroundFileChange = (e) => {
    setBackgroundFile(e.target.files[0]);
  };
  
  const removeBackgroundFile = () => {
    setBackgroundFile(null);
  };
  
  const handleGalleryFilesChange = (e) => {
    setGalleryFiles([...galleryFiles, ...Array.from(e.target.files)]);
  };
  
  const handleGalleryLinkChange = (index, value) => {
    const newLinks = [...galleryLinks];
    newLinks[index] = value;
    setGalleryLinks(newLinks);
  };
  
  const addGalleryLinkField = () => {
    if (galleryLinks.length < 10) {
      setGalleryLinks([...galleryLinks, '']);
    }
  };

  // Törlés a már létező galéria képek közül
  const handleDeleteExistingImage = async (imageId) => {
    try {
      const response = await fetch(url + `/api/utazasok/galeriakep/${imageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Nem sikerült törölni a képet.');
      }
      setExistingGalleryImages(prev => prev.filter(img => img.picture_id !== imageId));
    } catch (err) {
      console.error('Hiba a kép törlésekor:', err);
      alert('Hiba a kép törlésekor');
    }
  };
  
  // Adatok beküldése: PUT kérés a szerkesztett adatok mentéséhez
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);  // HTML tartalom
    formData.append('price', price);
    formData.append('tol', new Date(tol).toISOString().split('T')[0]);
    formData.append('ig', new Date(ig).toISOString().split('T')[0]);
    formData.append('repulos', repulos ? 'true' : 'false');

    if (backgroundFile) {
      formData.append('backgroundFile', backgroundFile);
    } else if (backgroundLink) {
      formData.append('background_picture', backgroundLink);
    }
    
    if (galleryFiles.length > 0) {
      galleryFiles.forEach(file => {
        formData.append('galleryFiles', file);
      });
    }
    galleryLinks.forEach((link, index) => {
      if (link.trim() !== "") {
        formData.append(`galleryLinks[${index}]`, link);
      }
    });
    
    // Debug: kilogoljuk a formData tartalmát
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }
    
    try {
      const response = await fetch(url + `/api/utazasok/${id}`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });
      if (!response.ok) {
        console.log('Hiba:', response.statusText);
        throw new Error('Nem sikerült frissíteni az utazás adatait.');
      }
      const result = await response.json();
      console.log('Sikeres frissítés:', result);
      navigate(`/travel/${id}`);
    } catch (err) {
      console.error('Frissítési hiba:', err);
      setError(err.message);
    }
  };

  if (!isAdmin) {
    return (
      <div className="error">
        Hozzáférés megtagadva. Csak adminisztrátorok számára elérhető.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loader-container">
        <BeatLoader
          color="#001662"
          size={30}
          loading={loading}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      </div>
    );
  }

  
  
  if (error) {
    return <div className="error">Hiba: {error}</div>;
  }
  
  return (
    <div className="travel-edit-container">
      <h2>Utazás szerkesztése</h2>
      <form onSubmit={handleSubmit} className="travel-edit-form">
        <label>Utazás címe:</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        <label>Leírás:</label>
        {/* ReactQuill szerkesztő */}
        <ReactQuill 
          theme="snow" 
          value={description} 
          onChange={setDescription} 
            modules={modules}
            formats={formats}
        />

        <label>Ár:</label>
        <input
          type="text"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
        />

        <label>Tól (dátum):</label>
        <input
          type="date"
          value={new Date(tol).toISOString().split('T')[0]}
          onChange={(e) => setTol(e.target.value)}
          required
        />

        <label>Ig (dátum):</label>
        <input
          type="date"
          value={new Date(ig).toISOString().split('T')[0]}
          onChange={(e) => setIg(e.target.value)}
          required
        />

        <label className="repulos-checkbox">
          <input
            type="checkbox"
            checked={repulos}
            onChange={(e) => setRepulos(e.target.checked)}
          />
          <span className="repulos-checkbox__icon" aria-hidden="true">✈</span>
          <span className="repulos-checkbox__text">
            Repülős út
            <small>Bepipálva a „Repülős utak” közé kerül, az egy/több napos listákban nem jelenik meg.</small>
          </span>
        </label>

        <h3>Háttérkép</h3>
        <label>Fájl feltöltése:</label>
        <input
          type="file"
          onChange={handleBackgroundFileChange}
          accept="image/*"
        />
        {backgroundFile && (
          <div className="background-preview">
            <img
              src={URL.createObjectURL(backgroundFile)}
              alt="Háttérkép előnézet"
              width="100"
              height="100"
            />
            <button className="torles-gomb" type="button" onClick={removeBackgroundFile}>
              Törlés
            </button>
          </div>
        )}
        <p>Vagy add meg az URL-t:</p>
        <input
          type="text"
          value={backgroundLink}
          onChange={(e) => setBackgroundLink(e.target.value)}
          placeholder="Háttérkép URL"
        />

        <h3>Galéria képek</h3>
        <label>Fájl feltöltés (több fájl is):</label>
        <input
          type="file"
          onChange={handleGalleryFilesChange}
          accept="image/*"
          multiple
        />
        {galleryFiles.length > 0 && (
          <div className="gallery-previews">
            {galleryFiles.map((file, index) => (
              <div key={index} className="gallery-preview">
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Galéria kép előnézet ${index + 1}`}
                  width="100"
                  height="100"
                />
                <button
                  className="torles-gomb"
                  type="button"
                  onClick={() => setGalleryFiles(galleryFiles.filter((_, i) => i !== index))}
                >
                  Törlés
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Megjelenítjük a már létező képeket (backendről) */}
        {existingGalleryImages.length > 0 && (
          <div className="existing-gallery">
            <h3>Már létező képek</h3>
            <div className="existing-gallery-grid">
              {existingGalleryImages.map((img) => (
                <div key={img.picture_id} className="existing-gallery-item">
                  <img
                    src={img.picture_url}
                    alt={`Kép ${img.picture_id}`}
                    width="100"
                    height="100"
                  />
                  <button
                    className="torles-gomb"
                    type="button"
                    onClick={() => handleDeleteExistingImage(img.picture_id)}
                  >
                    Törlés
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <p>Vagy add meg a képek URL-jeit:</p>
        {galleryLinks.map((link, index) => (
          <div key={index} className="gallery-link-field">
            <input
              type="url"
              placeholder={`Kép URL ${index + 1}`}
              value={link}
              onChange={(e) => handleGalleryLinkChange(index, e.target.value)}
            />
            {link && (
              <button
                className="torles-gomb"
                type="button"
                onClick={() => setGalleryLinks(galleryLinks.filter((_, i) => i !== index))}
              >
                Törlés
              </button>
            )}
          </div>
        ))}
        {galleryLinks.length < 10 && (
          <button className="gallery_link" type="button" onClick={addGalleryLinkField}>
            Új URL mező
          </button>
        )}

        <button className="submit-gomb" type="submit">
          Mentés
        </button>
      </form>
    </div>
  );
};

export default TravelEdit;
